import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import environment from '@environments/environment';
import { LoadingService } from '@loader/spinner';
import { AuthActions } from '../auth/redux/auth.actions';

/**
 * Jo (AI IRB) is served from this origin under `environment.joBaseUrl`'s path but owns no route
 * in this app's router, so an `originalUri` under that path must get a full page load here rather
 * than a `UrlTree` this app's router would fail to resolve and silently send to `/welcome`.
 */
const joOriginalUriPrefix = environment.joBaseUrl
  ? new URL(environment.joBaseUrl).pathname
  : null;

/** Reads `originalUri` before `handleRedirect` clears it, then redirects there. */
export const OktaLoginCallbackGuard: CanActivateFn = async () => {
  const oktaAuth: OktaAuth = inject(OKTA_AUTH);
  const router = inject(Router);
  const store = inject(Store);
  const loading = inject(LoadingService);

  const url = new URL(window.location.href);
  if (!url.searchParams.get('code') || !url.searchParams.get('state')) {
    window.location.href = environment.clinsphereUrl;
    return false;
  }

  loading.showLoading();
  const originalUri = oktaAuth.getOriginalUri();

  try {
    await oktaAuth.handleRedirect();
  } catch (err) {
    console.error('Error handling Okta redirect', err);
    store.dispatch(AuthActions.loginFailed({ error: err }));
    loading.hideLoading();
    return false;
  }

  store.dispatch(AuthActions.handleLoginRedirect());

  // Match on the pathname only -- a bare Jo root carrying a query or hash (e.g. `/submitassist?x=1`)
  // has no `/` right after the prefix, so matching against the raw `originalUri` would miss it.
  const originalUriPathname = originalUri?.split(/[?#]/)[0];
  if (
    joOriginalUriPrefix &&
    (originalUriPathname === joOriginalUriPrefix ||
      originalUriPathname?.startsWith(`${joOriginalUriPrefix}/`))
  ) {
    window.location.href = `${environment.appBaseHref}${originalUri}`;
    return false;
  }

  return router.parseUrl(
    originalUri && originalUri !== '/' ? originalUri : '/welcome',
  );
};
